import { useCallback, useMemo } from 'react';
import { GridSortModel } from '@mui/x-data-grid-premium';
import { useUrlState, codecs } from '../../hooks/useUrlState';
import { useReportDefaults } from '../../hooks/useReportDefaults';
import { ENTITY_FILTERS, ENTITY_FILTER_ORDER } from '../../components/entity-fetchers';
import { FilterEntry, fmtDate, levelDims, apiSortField, sortForLevel } from './tracking-page.format';
import { useFilterParams } from '../../hooks/useFilterParams';
import { initialDrilldownChain } from '../../utils/user-settings';

/**
 * Everything that defines "which report am I looking at", held in the URL so a
 * refresh — or a shared link — restores the exact view instead of resetting.
 *
 * Defaults come from the reader's own preferences, which makes an untouched view
 * reader-relative: a link with no `group` param opens on the recipient's default
 * grouping, not the sender's. A view the user actually changed carries its params
 * and travels unchanged.
 */
/** Changing any of these invalidates the current page, so it is dropped in the same write. */
const PAGE = ['page'];

export function useReportState() {
  const defaults = useReportDefaults();


  const [dateRange, setDateRange] = useUrlState<[Date | null, Date | null]>('dates', defaults.dateRange, codecs.dateRange, 'push', PAGE);
  const [groupBy, setGroupBy] = useUrlState('group', defaults.groupBy, codecs.str, 'push', PAGE);
  // Ordered levels below the grouping, e.g. ['offer_id', 'user_agent']. One string stays
  // readable in a link and decodes older single-value `drill=` params unchanged.
  const [drilldownChain, setDrilldownChain] = useUrlState<string[]>(
    'drill',
    initialDrilldownChain(defaults),
    codecs.csv,
    'push',
    PAGE,
  );
  const drilldownGroupBy = drilldownChain[0] || '';
  const [timezone, setTimezone] = useUrlState('tz', defaults.timezone, codecs.num, 'push', PAGE);
  const [excludeBots, setExcludeBots] = useUrlState('nobots', defaults.excludeBots, codecs.bool, 'push', PAGE);
  const [convByClick, setConvByClick] = useUrlState('convclick', defaults.conversionsByClick, codecs.bool, 'push', PAGE);
  // Compares against the period immediately before this one, of the same length.
  const [compare, setCompare] = useUrlState('cmp', false, codecs.bool, 'push', PAGE);
  const [tagIds, setTagIds] = useUrlState<string[]>('tags', [], codecs.json<string[]>(), 'push', PAGE);
  // The grid calls the entity column display_name; the API calls it name.
  const [sortModel, setSortModel] = useUrlState<GridSortModel>(
    'sort',
    [{ field: defaults.orderBy === 'name' ? 'display_name' : defaults.orderBy, sort: defaults.order }],
    codecs.sortModel,
    'replace',
    PAGE,
  );
  const [filters, setFilters] = useUrlState<FilterEntry[]>('filters', [], codecs.json<FilterEntry[]>(), 'push', PAGE);
  const [pagination, setPagination] = useUrlState('page', { page: 0, pageSize: 100 }, codecs.pageModel);

  // Which entity the filter box edits. Follows the grouping until picked explicitly.
  const [pickedField, setPickedField] = useUrlState('ffield', '', codecs.str);
  const firstDim = levelDims(groupBy)[0] ?? '';
  const boxField = pickedField || (ENTITY_FILTERS[firstDim] ? firstDim : ENTITY_FILTER_ORDER[0]);

  const dateFrom = dateRange[0] ? fmtDate(dateRange[0]) : fmtDate(new Date());
  const dateTo = dateRange[1] ? fmtDate(dateRange[1]) : fmtDate(new Date());

  const { filterParams, filtersKey } = useFilterParams(filters);

  const tagsParam = useMemo(() => {
    const p: Record<string, string[]> = {};
    if (tagIds.length) p.tags = tagIds;
    return p;
  }, [tagIds]);

  const sortBy = apiSortField(sortForLevel(sortModel, groupBy)[0]?.field);
  const sortOrder = sortModel[0]?.sort || 'desc';

  // Comparing annotates the rows in place and must not reorder them — the sort is the
  // reader's. Switching it off does have to move, though: the delta column goes with it.
  const toggleCompare = useCallback(() => {
    setCompare(!compare);
    if (compare && sortModel[0]?.field === 'profit_delta') {
      setSortModel([{ field: defaults.orderBy === 'name' ? 'display_name' : defaults.orderBy, sort: defaults.order }]);
    }
  }, [compare, setCompare, sortModel, setSortModel, defaults]);

  return {
    dateRange, setDateRange,
    groupBy, setGroupBy,
    drilldownGroupBy, drilldownChain, setDrilldownChain,
    timezone, setTimezone,
    excludeBots, setExcludeBots,
    convByClick, setConvByClick,
    compare, setCompare, toggleCompare,
    tagIds, setTagIds,
    sortModel, setSortModel,
    filters, setFilters,
    pagination, setPagination,
    boxField, setPickedField,
    dateFrom, dateTo,
    filterParams, filtersKey, tagsParam,
    sortBy, sortOrder,
  };
}

export type ReportState = ReturnType<typeof useReportState>;
